import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AdminAuthAPI from '../../api/admin/auth';

const AdminAuthCallback = () => {
  const [status, setStatus] = useState('processing'); // 'processing', 'success', 'error'
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hasAttempted = useRef(false); // Prevent duplicate authentication attempts

  useEffect(() => {
    const handleAdminAuthentication = async () => {
      // Prevent duplicate authentication attempts (React 18 Strict Mode issue)
      if (hasAttempted.current) {
        console.log('Admin authentication already attempted, skipping...');
        return;
      }
      hasAttempted.current = true;

      try {
        // Get the token from URL parameters
        const token = searchParams.get('token');
        const stytch_token_type = searchParams.get('stytch_token_type');
        
        // Debug logging
        console.log('Admin Auth Callback - URL Search Params:', Object.fromEntries(searchParams.entries()));
        console.log('Admin Auth Callback - Token:', token);
        console.log('Admin Auth Callback - Stytch Token Type:', stytch_token_type);
        
        if (!token) {
          setError('No authentication token found in URL');
          setStatus('error');
          return;
        }

        // Handle admin password reset tokens - redirect to password reset form instead of authenticating
        if (stytch_token_type === 'multi_tenant_passwords') {
          console.log('Admin password reset token detected, redirecting to reset password form');
          setStatus('success');
          
          // Store the token temporarily for the password reset form
          sessionStorage.setItem('admin_password_reset_token', token);
          
          // Redirect to admin password reset form
          setTimeout(() => {
            navigate(`/admin/auth/reset-password?token=${encodeURIComponent(token)}`);
          }, 1000);
          return;
        }

        // Handle admin magic link authentication
        console.log('Handling admin magic link authentication flow');
        
        // Use AdminAuthAPI to authenticate the magic link token
        const result = await AdminAuthAPI.authenticateMagicLink(token);
        
        setStatus('success');
        
        // Redirect to admin dashboard after a brief delay
        setTimeout(() => {
          navigate('/admin/dashboard');
        }, 2000);

      } catch (err) {
        console.error('Admin authentication error:', err);
        
        // Handle error - ensure error is always a string
        const errorMessage = err.response?.data?.message || 
                           err.response?.data?.detail || 
                           err.message || 
                           'Admin authentication failed';
        
        setError(typeof errorMessage === 'string' ? errorMessage : 'Admin authentication failed');
        setStatus('error');
      }
    };

    handleAdminAuthentication();
  }, [searchParams, navigate]);

  const handleRetry = () => {
    navigate('/admin/login');
  };

  return (
    <div style={{ 
      maxWidth: 400, 
      margin: '100px auto', 
      padding: 20, 
      textAlign: 'center',
      fontFamily: 'system-ui, -apple-system, sans-serif'
    }}>
      <h2 style={{ marginBottom: 30, color: '#1a1a1a' }}>Admin Portal</h2>
      
      {status === 'processing' && (
        <div>
          <div style={{ 
            fontSize: '48px', 
            marginBottom: 20,
            animation: 'spin 1s linear infinite'
          }}>
            ⏳
          </div>
          <h3 style={{ color: '#1a1a1a' }}>Authenticating Admin...</h3>
          <p style={{ color: '#666' }}>Please wait while we verify your admin login.</p>
        </div>
      )}

      {status === 'success' && (
        <div>
          <div style={{ 
            fontSize: '48px', 
            marginBottom: 20,
            color: '#4caf50'
          }}>
            ✅
          </div>
          <h3 style={{ color: '#4caf50' }}>
            {searchParams.get('stytch_token_type') === 'multi_tenant_passwords' 
              ? 'Password Reset Link Verified!'
              : 'Admin Login Successful!'}
          </h3>
          <p style={{ color: '#666' }}>
            {searchParams.get('stytch_token_type') === 'multi_tenant_passwords'
              ? 'Redirecting to password reset form...'
              : 'Redirecting to admin dashboard...'}
          </p>
        </div>
      )}

      {status === 'error' && (
        <div>
          <div style={{ 
            fontSize: '48px', 
            marginBottom: 20,
            color: '#f44336'
          }}>
            ❌
          </div>
          <h3 style={{ color: '#f44336' }}>Admin Authentication Failed</h3>
          <p style={{ color: '#666', marginBottom: 20 }}>{error}</p>
          
          <button 
            onClick={handleRetry}
            style={{ 
              padding: '12px 24px', 
              backgroundColor: '#3f51b5', 
              color: 'white', 
              border: 'none', 
              borderRadius: 4,
              fontSize: '16px',
              cursor: 'pointer',
              fontWeight: 500
            }}
          >
            Back to Admin Login
          </button>
        </div>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default AdminAuthCallback; 